<template>
  <DsPageFooter>
    <template #primaryNavigation>
      <DsFooterPrimaryNavigation
        v-if="variant === 'primaryNavigation' && footerItems && footerItems.items.primaryItems"
        :items="footerItems.items.primaryItems"
        :screen="screen"
      >
        <template #socialBar>
          <DsSocialBar
            v-if="socialBarItems"
            :items="socialBarItems"
            disposition="horizontal"
          />
        </template>
        <template #signup>
          <DsLink
            v-if="newsletterSignUp"
            :content="newsletterSignUp.title"
            :url="newsletterSignUp.url"
            :open-in-new-window="newsletterSignUp.openInNewWindow"
            :nuxt="newsletterSignUp.nuxt"
            type="button"
            variant="secondary"
          />
        </template>
      </DsFooterPrimaryNavigation>
      <p
        v-if="variant === 'homepageNavigation'"
        class="ds-color-white"
      >
        {{ labels.goTo }}
        <DsLink
          :content="labels.brandDomain || ''"
          type="link"
          color="white"
          variant="secondary"
          :url="mainSiteUrl"
        />
      </p>
    </template>
    <template
      v-if="footerItems?.items?.secondaryItems"
      #secondaryNavigation
    >
      <DsFooterSecondaryNavigation :items="footerItems.items.secondaryItems" />
    </template>
    <template #localeSwitcher>
      <DsDropdown
        v-show="localeOptions.length > 0"
        theme="light"
        :label="labels.languageSwitcher"
        :text="currentLocale"
        :icon="{ name: 'globe', category: 'various' }"
        align-y="top"
        :align-x="dropdownAlignX"
        :options="localeOptions"
        height="260"
        :with-bold-text="false"
        @dropdown-item-clicked="handleDropdownItemClicked"
      />
      <span v-show="false">Beta version</span>
    </template>
    <template
      v-if="footerItems?.items?.legalDisclaimer"
      #legalDisclaimer
    >
      <RichText :data="footerItems.items.legalDisclaimer" />
    </template>
  </DsPageFooter>
</template>

<script setup>
import {
  DsDropdown,
  DsFooterPrimaryNavigation,
  DsFooterSecondaryNavigation,
  DsLink,
  DsPageFooter,
  DsSocialBar
} from 'awds'
import { useLocales } from '../../utils/useLocales'

const { isScreenSmall, screen } = useScreen()
const { locale } = useI18n()
const { locales } = useLocales(useRuntimeConfig().public.targetSite)
const { labels } = useGlobalData()
const mainSiteUrl = useRuntimeConfig().public.mainSiteUrl
const switchLocalePath = useSwitchLocalePath()

const props = defineProps({
  variant: {
    type: String,
    default: 'primaryNavigation'
  },
  footerData: {
    type: Object,
    default: null
  }
})

const footerItems = computed(() => props.footerData)

const socialBarItems = computed(() => {
  return footerItems.value.items?.socialItems
})

const newsletterSignUp = computed(() => {
  return footerItems.value.items?.newsletterSignUp
})

const currentLocale = computed(() => {
  const _locale = locales.find((loc) => loc.code === locale.value) || locale.value
  return `${_locale.country} (${_locale.language})`
})

const localeOptions = computed(() => {
  // Get page locales
  // TODO: disabled state on locales that are not available for the page
  // TODO: OR categories for locales that are available or not
  // TODO: OR error page that tells you that the locale is not available
  const allLocales = locales.map((l) => {
    const path = switchLocalePath(l.code)
    return {
      value: l.code,
      path: path,
      text: `${l.country} (${l.language})`
    }
  })

  const pageLocales = allLocales.filter((locale) => !!locale.path)

  // on preview all locales
  if (useRuntimeConfig().public.preview && import.meta.client) {
    return allLocales
  }

  // return all locales when there is more than 1 slug with a locale
  if (pageLocales?.length > 1) {
    return pageLocales
  }

  return allLocales
})

const dropdownAlignX = computed(() => {
  return isScreenSmall.value ? 'left' : 'right'
})

const handleDropdownItemClicked = ({ path }) => {
  // As the OneTrust cookie related script has to be reloaded
  navigateTo(path, { replace: true, external: true })
}
</script>
